import { HttpClient } from '@angular/common/http';
import { inject, Injectable, OnDestroy, Signal, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ComparisonOperator, TaskStatus } from '@common/enums';
import { TaskManagerOdata } from '@common/models';
import { ODataPagedResult } from '@common/odata';
import { AbilityService } from '@common/services/auth/ability.service';
import { AuthContextService } from '@common/services/auth/auth-context.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { merge, Observable, Subscription, switchMap, timer } from 'rxjs';
import { catchError } from 'rxjs/operators';

const POLLING_INTERVAL = 60000;
const INITIAL_DELAY = 2000;
const COUNT_KEY = '@odata.count';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class FailedTaskPollingService implements OnDestroy {
  private readonly ability = inject(AbilityService);
  private readonly authContext = inject(AuthContextService);
  private readonly http = inject(HttpClient);

  readonly #failedTask = signal<number | null>(null);
  readonly #isPolling = signal<boolean>(false);
  readonly #refreshTrigger = signal<boolean>(false);

  protected readonly authUserId = this.authContext.id;
  protected readonly authDomainId = this.authContext.domainId;

  protected readonly isAdmin = this.ability.isAdmin;

  private pollingSub?: Subscription;
  private timer$ = timer(INITIAL_DELAY, POLLING_INTERVAL);
  private refresh$ = toObservable(this.#refreshTrigger);

  get failedTask(): Signal<number | null> {
    return this.#failedTask.asReadonly();
  }

  get isPolling(): Signal<boolean> {
    return this.#isPolling.asReadonly();
  }

  startPolling(): void {
    this.stopPolling();
    this.#isPolling.set(true);

    this.pollingSub = merge(this.timer$, this.refresh$)
      .pipe(switchMap(() => this.getQueryTasks().pipe(catchError(() => [{ COUNT_KEY: 0 }]))))
      .subscribe({
        next: (res) => this.#failedTask.set(res?.[COUNT_KEY] ?? 0),
        complete: () => this.#isPolling.set(false),
        error: () => this.#isPolling.set(false)
      });
  }

  private getQueryTasks(): Observable<ODataPagedResult<TaskManagerOdata>> {
    return this.http.get<ODataPagedResult<TaskManagerOdata>>(this.buildQueryTasksUrl());
  }

  private buildQueryTasksUrl(): string {
    const baseUrl = '/api/odata/';
    const isAdmin = this.isAdmin();
    const id = isAdmin ? this.authDomainId() : this.authUserId();
    const label = isAdmin ? 'Domains' : 'BackupUsers';

    return `${baseUrl + label}(${id})/QueryTasks?$filter=State ${ComparisonOperator.Eq} '${TaskStatus.Failed}'&$top=0&$count=true`;
  }

  refreshNow(): void {
    this.#refreshTrigger.set(true);
  }

  stopPolling(): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = undefined;

    this.#isPolling.set(false);
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }
}
